import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class StationRoleOverride extends BaseObj {
    public ID: number;
    public __id: number;

    public ActiveFrom: Date;
    public __activefrom: Date;

    public ActiveTo: Date;
    public __activeto: Date;

    public OnlyGpiOrManual: boolean | null;
    public __onlygpiormanual: boolean | null;

    public StationRoleID: number;
    public __stationroleid: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        ActiveFrom?: Date;
        ActiveTo?: Date;
        EntityState?: EntityState;
        OnlyGpiOrManual?: boolean | null;
        StationRoleID?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StationRoleOverride>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StationRoleOverride tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.ActiveFrom = values.ActiveFrom ?? new Date('1970-01-01T00:00:00.000');
        this.__activefrom = values.ActiveFrom ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'ActiveFrom', {
            get(): Date {
                return this.__activefrom;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('ActiveFrom', this.__activefrom, value);
                this.__activefrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ActiveTo = values.ActiveTo ?? new Date('1970-01-01T00:00:00.000');
        this.__activeto = values.ActiveTo ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'ActiveTo', {
            get(): Date {
                return this.__activeto;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('ActiveTo', this.__activeto, value);
                this.__activeto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OnlyGpiOrManual = values.OnlyGpiOrManual ?? null;
        this.__onlygpiormanual = values.OnlyGpiOrManual ?? null;
        Object.defineProperty(this, 'OnlyGpiOrManual', {
            get(): boolean | null {
                return this.__onlygpiormanual;
            },
            set(value: boolean | null) {
                this.__entityStateHandler.propertyChanged('OnlyGpiOrManual', this.__onlygpiormanual, value);
                this.__onlygpiormanual = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoleID = values.StationRoleID ?? 0;
        this.__stationroleid = values.StationRoleID ?? 0;
        Object.defineProperty(this, 'StationRoleID', {
            get(): number {
                return this.__stationroleid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationRoleID', this.__stationroleid, value);
                this.__stationroleid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"ActiveFrom": ${'"' + formatDateIso(this.ActiveFrom) + '"'}` +
            ',' +
            `"ActiveTo": ${'"' + formatDateIso(this.ActiveTo) + '"'}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"OnlyGpiOrManual": ${JSON.stringify(this.OnlyGpiOrManual)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StationRoleOverride from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        ActiveFrom?: Date;
        ActiveTo?: Date;
        OnlyGpiOrManual?: boolean | null;
        StationRoleID?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): StationRoleOverride {
        return new StationRoleOverride(values);
    }

    public static _FromServerJson(json: any): StationRoleOverride {
        return new StationRoleOverride({
            ID: json.ID,
            ActiveFrom: json.ActiveFrom instanceof Date ? json.ActiveFrom : new Date(json.ActiveFrom),
            ActiveTo: json.ActiveTo instanceof Date ? json.ActiveTo : new Date(json.ActiveTo),
            OnlyGpiOrManual: json.OnlyGpiOrManual ?? null,
            StationRoleID: json.StationRoleID,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StationRoleOverride from another instance of StationRoleOverride.
     * @param other The StationRoleOverride you want to clone
     * @returns A cloned StationRoleOverride
     */
    public static FromOther(other: StationRoleOverride): StationRoleOverride {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StationRoleOverride._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StationRoleOverride.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'ActiveFrom' | 'ActiveTo' | 'OnlyGpiOrManual' | 'StationRoleID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StationRoleOverride';
}
